import { ApisauceInstance } from 'apisauce';

import isDefined from 'isDefined';

import getDriverRewardsOutput from '../../api/getDriverRewardsOutput';
import fetchWeeklyRewardRecords from '../../utils/fetchWeeklyRewardRecords';

/**
 * A single week's full reward doc forwarded under a sub-period period's
 * `subPeriods`. It is the same shape as a `PeriodBonusDocument`'s reward output
 * (rewards / criteria / qualifiers, totals, dates, and — on WEL's runtime shape
 * — `totalPay`/`daysRemaining`), with an `id` alias of its own weekly
 * `bonusDatasetRecordId` so the embed can key + navigate to that week.
 */
export type WeeklyBonusDocument = BonusTypes.Rewards.AnyDriverRewardsOutput & {
  id: string;
};

/**
 * A reward output forwarded to the embed as a period document. On top of the
 * raw reward output it carries:
 *   - `id` — alias of `bonusDatasetRecordId`, the embed's dedup key.
 *   - `periodStatus` — the period's backend status, so the embed keys picker
 *     labels + current-period detection on status (like the native app) rather
 *     than deriving from dates.
 *   - `subPeriods` — for sub-period programmes only (e.g. WEL's weekly windows
 *     in a 4-weekly sign-off period), the FULL per-week reward docs that make up
 *     this sign-off period (not just the window metadata) — the embed renders a
 *     card per week and drills into each week's detail, so it needs the whole
 *     doc. Absent for single-window programmes, so the field is inert for every
 *     carrier except those using the sub-period pattern.
 */
export type PeriodBonusDocument = BonusTypes.Rewards.AnyDriverRewardsOutput & {
  id: string;
  periodStatus: BonusTypes.PeriodStatus;
  subPeriods?: WeeklyBonusDocument[];
};

/**
 * The full per-week reward docs for one sub-period (sign-off) period. The
 * per-week records live in the `driverBonusRewards` Firestore collection — one
 * doc per driver per week, all sharing the parent `bonusPeriodId` — so a single
 * query returns every week of the period, which we order by window start.
 *
 * `GET /rewards` only addresses the parent period (a single record), so this
 * read is the only way to get the weekly breakdown. The collection is
 * deny-by-default in firestore.rules and opened only for the account(s) using
 * the sub-period pattern (currently WEL) — and this is called only for periods
 * that actually carry `subPeriods`, so no other account hits the read. A failed
 * read degrades to no weeks (the period still shows as its single record).
 */
const fetchPeriodWeeks = async ({
  accountId,
  driverId,
  bonusConfigDocId,
  periodId,
}: {
  accountId: string;
  driverId: string;
  bonusConfigDocId: string;
  periodId: string;
}): Promise<WeeklyBonusDocument[]> => {
  const records = await fetchWeeklyRewardRecords({
    accountId,
    driver: driverId,
    bonusProgramId: bonusConfigDocId,
    bonusPeriodId: periodId,
  });
  return records.map((record) => ({
    ...record,
    id: record.bonusDatasetRecordId,
  }));
};

/**
 * Gathers every existing reward record for a driver + program across ALL of the
 * program's (non-archived) periods — the full multi-year history the embed's
 * period picker browses (no year scoping). There is no batch endpoint, so we
 * fan out one `getDriverRewardsOutput` per period and drop the periods that
 * have no record yet (the embed renders those as "upcoming" itself).
 *
 * For sub-period programmes (e.g. WEL) each period additionally carries its full
 * per-week reward docs under `subPeriods`, read from Firestore (see
 * `fetchPeriodWeeks`). When the parent period has no single REST record but the
 * weekly docs exist, the driver's most recent week stands in as the period doc
 * so the period isn't dropped from the picker.
 *
 * COST: this is one gateway call per period, unbounded by year — a 4-week
 * program (13 periods/year) over several years can be 30+ parallel calls per
 * PERIODS_REQUEST (plus one Firestore read per sub-period period). Acceptable at
 * current scale, but if programs accrue many years of history this should move
 * to a batch endpoint (or a bounded window), rather than fanning out the entire
 * history every time.
 */
const fetchProgramPeriodDocuments = async ({
  api,
  accountId,
  driverId,
  bonusConfigDocId,
  periods,
}: {
  api: ApisauceInstance;
  accountId: string;
  driverId: string;
  bonusConfigDocId: string;
  periods: BonusTypes.BonusPeriod[];
}): Promise<PeriodBonusDocument[]> => {
  const results = await Promise.all(
    periods.map(async (period): Promise<PeriodBonusDocument | undefined> => {
      const [weeks, response] = await Promise.all([
        // Only sub-period periods carry `subPeriods`; ordinary periods skip the
        // Firestore read entirely (and so never hit the deny-by-default rule).
        period.subPeriods
          ? fetchPeriodWeeks({
              accountId,
              driverId,
              bonusConfigDocId,
              periodId: period.id,
            })
          : Promise.resolve<WeeklyBonusDocument[]>([]),
        getDriverRewardsOutput({
          api,
          driver: driverId,
          bonusProgramId: bonusConfigDocId,
          intervalId: period.id,
        }).catch(() => undefined),
      ]);

      const restRecord =
        response?.ok && response.data?.bonusDatasetRecordId
          ? response.data
          : undefined;

      // Base period doc: the REST parent record, or — for a sub-period period
      // whose parent has no single REST record — the most recent week (weeks are
      // ordered ascending by start date). No record at all → drop the period.
      const base = restRecord ?? weeks[weeks.length - 1];
      if (!base) {
        return undefined;
      }

      const doc: PeriodBonusDocument = {
        ...base,
        id: base.bonusDatasetRecordId,
        periodStatus: period.status,
      };
      return weeks.length > 0 ? { ...doc, subPeriods: weeks } : doc;
    }),
  );

  return results.filter(isDefined);
};

export default fetchProgramPeriodDocuments;
